if (!customElements.get('product-quick-nav')) {
  /**
   * Controls directional scrolling without coupling to product item content.
   */
  class ProductQuickNav extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.viewport = this.querySelector('[data-product-quick-nav-viewport]');
      this.track = this.querySelector('[data-product-quick-nav-track]');
      this.previousButton = this.querySelector('[data-product-quick-nav-prev]');
      this.nextButton = this.querySelector('[data-product-quick-nav-next]');
      this.previousLayer = this.querySelector('[data-product-quick-nav-prev-layer]');
      this.nextLayer = this.querySelector('[data-product-quick-nav-next-layer]');

      if (!this.viewport || !this.track) return;

      this.handlePreviousClick = () => this.scrollByItem(-1);
      this.handleNextClick = () => this.scrollByItem(1);
      this.handleScroll = () => this.queueControlsSync();
      this.handleControlsSync = () => {
        this.syncFrame = null;
        this.syncControls();
      };

      this.previousButton?.addEventListener('click', this.handlePreviousClick);
      this.nextButton?.addEventListener('click', this.handleNextClick);
      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.queueControlsSync());
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.track);
      }

      this.isInitialized = true;
      this.queueControlsSync();
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.previousButton?.removeEventListener('click', this.handlePreviousClick);
      this.nextButton?.removeEventListener('click', this.handleNextClick);
      this.viewport?.removeEventListener('scroll', this.handleScroll);
      this.resizeObserver?.disconnect();

      if (this.syncFrame) {
        cancelAnimationFrame(this.syncFrame);
      }

      this.syncFrame = null;
      this.resizeObserver = null;
      this.isInitialized = false;
    }

    /**
     * Coalesces native scroll and resize updates into one visual refresh.
     */
    queueControlsSync() {
      if (this.syncFrame) {
        cancelAnimationFrame(this.syncFrame);
      }

      this.syncFrame = requestAnimationFrame(this.handleControlsSync);
    }

    /**
     * Returns the measured width of one rendered product plus the active gap.
     * @returns {number}
     */
    getScrollStep() {
      const firstRenderedItem = Array.from(this.track.children).find(
        (item) => !item.hidden && getComputedStyle(item).display !== 'none'
      );
      const itemWidth = firstRenderedItem?.getBoundingClientRect().width || 0;
      const trackStyle = getComputedStyle(this.track);
      const gap = Number.parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;

      return itemWidth + gap;
    }

    /**
     * Moves exactly one rendered product, clamped to native scroll bounds.
     * @param {-1 | 1} direction
     */
    scrollByItem(direction) {
      const step = this.getScrollStep();
      if (step <= 0) return;

      const maximumScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const targetLeft = Math.min(maximumScroll, Math.max(0, this.viewport.scrollLeft + direction * step));

      this.viewport.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      });
    }

    /**
     * Keeps each arrow and gradient visible only while that direction can move.
     */
    syncControls() {
      const tolerance = 2;
      const maximumScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const canScrollPrevious = maximumScroll > tolerance && this.viewport.scrollLeft > tolerance;
      const canScrollNext =
        maximumScroll > tolerance && this.viewport.scrollLeft < maximumScroll - tolerance;

      this.setDirectionVisibility(this.previousButton, this.previousLayer, canScrollPrevious);
      this.setDirectionVisibility(this.nextButton, this.nextLayer, canScrollNext);
    }

    /**
     * @param {HTMLElement | null} button
     * @param {HTMLElement | null} layer
     * @param {boolean} isVisible
     */
    setDirectionVisibility(button, layer, isVisible) {
      if (button) button.hidden = !isVisible;
      if (layer) layer.hidden = !isVisible;
    }
  }

  customElements.define('product-quick-nav', ProductQuickNav);
}
